import { StyleSheet } from "react-native";

import type { UiTheme } from "../theme";

// Styles for the shared Workouts history list (WorkoutsView + its row /
// month-header). Ported from the mobile screen's workoutsListStyles (Track R)
// minus the logger-only banners (MAX-bump / outbox queue / New button), which
// stay in the mobile mount. Keyed off UiTheme so mobile (useTheme) and the
// web-rnw console (resolveTheme) share one tree.
export function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    flex: { flex: 1 },
    rowWrap: {
      flexDirection: "row",
      alignItems: "stretch",
    },
    // Lift the hovered row above its siblings so the full-name bubble paints
    // over the next row instead of being covered by it (web-only; inert on
    // mobile where nothing hovers).
    rowWrapHover: {
      zIndex: 50,
    },
    row: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s4,
    },
    rowPressed: {
      backgroundColor: p.surface2,
    },
    // Web master-detail highlight for the selected row: brand100 tint + bold
    // name, paired with accessibilityState selected so the cue never reads by
    // colour alone (reduced-vision a11y). No side-stripe accent (banned).
    rowSelected: {
      backgroundColor: p.brand100,
    },
    rowDateBlock: {
      width: 52,
      alignItems: "center",
      justifyContent: "center",
    },
    rowDateDay: {
      color: p.ink,
      fontSize: t.sizes.xl,
      fontWeight: "700",
      letterSpacing: -0.4,
      fontVariant: ["tabular-nums"],
    },
    rowDateDow: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.6,
    },
    rowMain: {
      flex: 1,
      gap: 2,
    },
    rowName: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: "500",
    },
    rowNameSelected: {
      fontWeight: "700",
    },
    rowSecondary: {
      color: p.ink3,
      fontSize: t.sizes.sm,
    },
    // Duration + provenance badge share one line so rows stay uniform height.
    rowSecondaryRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s2,
    },
    // "Imported" provenance tag for Health-Connect-sourced sessions. Off the
    // brand (it's lineage, not a primary/now state — colour budget): a quiet
    // surface2 chip with ink3 text. Paired with an a11y label so it never
    // reads by appearance alone.
    hcBadge: {
      backgroundColor: p.surface2,
      borderRadius: r.r2,
      paddingHorizontal: s.s2,
      paddingVertical: 1,
    },
    hcBadgeText: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: "600",
    },
    // Web-only hover affordance for the row (onHoverIn never fires on touch,
    // so this is inert on mobile). A quiet surface2 tint, never on a selected
    // row (brand100 must keep reading as the selection cue).
    rowHover: {
      backgroundColor: p.surface2,
    },
    // Hover bubble carrying the FULL workout name when the row truncates it
    // (uniform single-line rows can't show a long name inline — web reveals it
    // on hover, mobile reads it on open). Anchored below rowMain, within the
    // row's width so it can't clip horizontally; dark bubble like HeaderTooltip,
    // text at base size for reduced-vision readability.
    nameTip: {
      position: "absolute",
      top: "100%",
      left: 0,
      marginTop: s.s1,
      maxWidth: 280,
      backgroundColor: p.ink,
      borderRadius: r.r2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      zIndex: 50,
      elevation: 8,
    },
    nameTipText: {
      color: p.bg,
      fontSize: t.sizes.base,
      lineHeight: t.sizes.base * t.leading.base,
      fontWeight: "500",
    },
    sectionHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      // 44pt floor: the collapse toggle is tapped on every scroll, so it must
      // clear the a11y tap-target minimum (reduced-vision hard constraint).
      minHeight: 44,
      backgroundColor: p.surface2,
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderColor: p.border,
    },
    sectionHeaderPressed: {
      opacity: 0.7,
    },
    sectionTitle: {
      color: p.ink2,
      fontSize: t.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    sectionRight: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s3,
    },
    sectionCount: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      fontVariant: ["tabular-nums"],
    },
    sectionChevron: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      width: 12,
      textAlign: "center",
    },
    repeatButton: {
      width: 56,
      alignItems: "center",
      justifyContent: "center",
      borderLeftWidth: StyleSheet.hairlineWidth,
      borderLeftColor: p.border,
    },
    repeatButtonPressed: {
      backgroundColor: p.surface2,
    },
    repeatButtonText: {
      color: p.ink2,
      fontSize: t.sizes.lg,
      fontWeight: "700",
    },
    separator: {
      height: StyleSheet.hairlineWidth,
      marginLeft: s.s5,
      backgroundColor: p.border,
    },
    center: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: s.s5,
    },
    errorText: {
      color: p.danger,
      fontSize: t.sizes.sm,
    },
    emptyText: {
      color: p.ink3,
      fontSize: t.sizes.base,
    },
    footer: {
      paddingVertical: s.s5,
      alignItems: "center",
    },
    footerText: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      letterSpacing: 0.6,
    },
  });
}

export type WorkoutsStyles = ReturnType<typeof makeStyles>;
